import React, { Fragment, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Row, Col, Card, CardBody, CardHeader, FormGroup, Label, Modal, ModalHeader, ModalBody, ModalFooter, Table } from 'reactstrap';
import { H5, Btn } from '../../../../AbstractElements';
import axios from 'axios';
import config from '../../../../config';
import Papa from 'papaparse';
import DataTable from 'react-data-table-component';
import Widgets1 from '../../../Common/CommonWidgets/Widgets1';
import { toast } from 'react-toastify';

const UploadMonthlySalesView = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const fileRef = useRef();

    const token = localStorage.getItem('sessiontoken');
    const login_name = localStorage.getItem("loginName");
    const configheader = {
        headers: {
            Authorization: `${token}`
        }
    };
    const concessionaire_id = localStorage.getItem("concessionaireId");
    const { property_id, month, year, audit_period_id, property_name } = location.state || {};
    const TOLERANCE = Number(localStorage.getItem("auditTolerance") || 0);

    const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];

    const [file, setFile] = useState(null);
    const [error, setError] = useState('');
    const [resultModal, setResultModal] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const login_id = localStorage.getItem("loginId");
    const handleFileChange = (f) => {
        if (!f) return;
        if (!f.name.match(/\.(xlsx|csv)$/i)) {
            setError(t('Only .xlsx and .csv files are supported.'));
            return;
        }
        setError('');
        setFile(f);
    };

    const handleTemplateDownload = () => {
        const csvContent = `mall_code,tenant_code,outlet_code,gross_sales,net_sales,tax,discount,returns`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', 'monthly_sales_template.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const insertToLog = (task) => {
        const data = {
            created_by: login_id,
            concessionaire_id: concessionaire_id,
            module: "Audit",
            task: `${login_name} ${task}`
        }
        axios
            .post(`${config.hostname}/log`, data, configheader)
            .then(() => {
                console.log("inserted to log table")
            })
            .catch((error) => {
                toast.error(t('Error'))
                console.log(error);

            });
    }

    const handleUpload = () => {
        if (!file) {
            setError(t('Please select a file before uploading'));
            return;
        }


        setError('');

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                try {

                    const rows = results.data;
                    if (!rows || rows.length === 0) { setError(t('Uploaded file is empty.')); return; }

                    const REQUIRED_COLUMNS = [
                        "mall_code",
                        "tenant_code",
                        "outlet_code",
                        "gross_sales",
                        "net_sales",
                        "tax",
                        "discount",
                        "returns"
                    ];

                    const NUMBER_COLUMNS = [
                        "gross_sales",
                        "net_sales",
                        "tax",
                        "discount",
                        "returns"
                    ];
                    const headers = Object.keys(rows[0] || {});

                    // Invalid structure
                    const missingColumns = REQUIRED_COLUMNS.filter(col => !headers.includes(col));

                    if (missingColumns.length > 0) {
                        setError(t('Invalid file structure. Please use the provided template.'));
                        return;
                    }

                    // Check blank values
                    const hasBlank = rows.some(row =>
                        REQUIRED_COLUMNS.some(col =>
                            row[col] === undefined ||
                            row[col] === null ||
                            String(row[col]).trim() === ""
                        )
                    );

                    if (hasBlank) {
                        setError(t('Uploaded file contains blank values. Please ensure all fields are filled.'));
                        return;
                    }

                    // Check numeric values
                    const invalidNumber = rows.some(row =>
                        NUMBER_COLUMNS.some(col => isNaN(Number(row[col])))
                    );

                    if (invalidNumber) {
                        setError(t('Sales values must contain valid numbers only.'));
                        return;
                    }

                    // Format data
                    const formattedData = rows.map(r => ({
                        mall_code: r.mall_code,
                        tenant_code: r.tenant_code,
                        outlet_code: r.outlet_code,
                        UPLOADED: {
                            gross_sales: Number(r.gross_sales),
                            net_sales: Number(r.net_sales),
                            tax: Number(r.tax),
                            discount: Number(r.discount),
                            returns: Number(r.returns)
                        }
                    }));

                    axios.post(
                        `${config.hostname}/audit/createtenantaudit`,
                        {
                            audit_period_id,
                            concessionaire_id: concessionaire_id,
                            property_id,
                            year,
                            month,
                            login_id,
                            tolerance: TOLERANCE,
                            data: formattedData
                        },
                        {
                            headers: {
                                Authorization: token,
                                'Content-Type': 'application/json'
                            }
                        }
                    )
                        .then(res => {

                            setUploadResult(res.data);
                            setResultModal(true);
                            insertToLog(`File uploaded for ${property_name} - ${monthNames[month]} ${year}`);
                        })
                        .catch(err => {

                            setError(err.response?.data?.message || t('Upload failed.'));
                        });

                } catch {
                    setError(t('Failed to process file.'));
                }
            },
            error: () => {
                setError(t('CSV parsing failed.'));
            }
        });
    };

    const handleReset = () => {
        setFile(null);
        setError('');
        if (fileRef.current) fileRef.current.value = '';
    };

    const handleBackToDashboard = () => {
        setResultModal(false);

        navigate(`${process.env.PUBLIC_URL}/admin/audit/default`, {
            state: {
                property_id,
                property_name,
                month,
                year
            }
        });
    };

    const failedColumns = [
        {
            name: t('Tenant Code'),
            selector: row => row.tenant_code || '-',
            sortable: true
        },
        {
            name: t('Outlet Code'),
            selector: row => row.outlet_code || '-',
            sortable: true
        },
        {
            name: t('Reason'),
            selector: row => row.reason || '-',
            cell: row => (
                <span className="txt-danger fw-bold">
                    {row.reason}
                </span>
            )
        }
    ];
    <style>
        {`
            .upload-summary-modal .modal-content{
            background: rgba(255,255,255,0.96);
            backdrop-filter: blur(6px);
            border-radius: 12px;
            border: 1px solid rgba(0,0,0,0.06);
            box-shadow: 0 12px 28px rgba(0,0,0,0.12);
            }

            .modal-backdrop.show{
            opacity:0.3;
            }
        `}
    </style>
    return (
        <Fragment>
            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <H5>{property_name} — {monthNames[month] ? t(monthNames[month]) : ''} {year}</H5>
                        <Btn attrBtn={{ color: 'primary', onClick: handleBackToDashboard }}>
                            {t('Back')}
                        </Btn>
                    </div>
                </CardHeader>

                <CardBody>
                    <Row>
                        <Card>
                            {/* Download Template */}
                            <Col md="12">
                                <FormGroup>
                                    <Label className="fw-bold mb-2">{t('Download Template')}</Label>
                                    <p>
                                        {t('Use the official template to ensure correct column mapping. Do not modify column headers.')}
                                    </p>
                                    <div>
                                        <Btn attrBtn={{ color: 'primary', onClick: handleTemplateDownload }}>
                                            {t('Download Template (.csv)')}
                                        </Btn>
                                    </div>
                                </FormGroup>
                            </Col>
                        </Card>
                        {/* File Drop Zone */}
                        <Card>
                            <Col md="12" className="mt-4">
                                <FormGroup>
                                    <Label className="fw-bold mb-2">{t('Select File')}</Label>
                                    <div
                                        onClick={() => fileRef.current?.click()}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => { e.preventDefault(); handleFileChange(e.dataTransfer.files[0]); }}
                                        className="border rounded text-center p-4"
                                        style={{
                                            borderStyle: 'dashed',
                                            borderColor: file ? 'var(--theme-default, #7366ff)' : '#ced4da',
                                            background: file ? 'rgba(115,102,255,0.05)' : '#f8f9fa',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        {file ? (
                                            <>
                                                <p className="mb-0 fw-bold" style={{ color: 'var(--theme-default, #7366ff)' }}>{file.name}</p>
                                                <small className="text-muted">{(file.size / 1024).toFixed(1)} KB · {t('Click to change')}</small>
                                            </>
                                        ) : (
                                            <>
                                                <div style={{ fontSize: 28, marginBottom: 8 }}>
                                                    📂
                                                </div>
                                                <p className="mb-0 fw-bold">{t('Drop your file here, or click to browse')}</p>
                                                <small className="text-muted">{t('Supports .csv')}</small>
                                            </>
                                        )}
                                        <input ref={fileRef} type="file" accept=".xlsx,.csv" style={{ display: 'none' }}
                                            onChange={(e) => handleFileChange(e.target.files[0])} />
                                    </div>
                                    {error && <small className="text-danger mt-1 d-block">{error}</small>}
                                </FormGroup>
                            </Col>

                        </Card>
                        {/* Action Buttons */}
                        <Col md="12">
                            <Btn attrBtn={{ color: 'primary', className: 'me-3', onClick: handleUpload }}>
                                {t('Upload File')}
                            </Btn>
                            {file && (
                                <Btn attrBtn={{ color: 'primary', className: 'me-3', onClick: handleReset }}>{t('Reset')}</Btn>
                            )}
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {uploadResult && (
                <Modal
                    className="modal-lg modal-dialog-centered upload-summary-modal"
                    isOpen={resultModal}
                    toggle={handleBackToDashboard}
                    backdrop="static"
                >

                    <ModalHeader toggle={handleBackToDashboard}>
                        <i className="fa fa-upload me-2"></i>
                        {t('Upload Summary')}
                    </ModalHeader>

                    <ModalBody>

                        {/* Dashboard Widgets */}
                        <Row className="mb-2">

                            <Col xl="3" sm="6">
                                <Widgets1
                                    data={{
                                        title: t('Total Tenants'),
                                        total: uploadResult.upload_summary?.total_rows_uploaded,
                                        color: 'primary',
                                        icon: 'customers'
                                    }}
                                />
                            </Col>

                            <Col xl="3" sm="6">
                                <Widgets1
                                    data={{
                                        title: t('Matched'),
                                        total: uploadResult.upload_summary?.matched,
                                        color: 'success',
                                        icon: 'customers'
                                    }}
                                />
                            </Col>

                            <Col xl="3" sm="6">
                                <Widgets1
                                    data={{
                                        title: t('Not Matched'),
                                        total: uploadResult.upload_summary?.not_matched,
                                        color: 'warning',
                                        icon: 'customers'
                                    }}
                                />
                            </Col>

                            <Col xl="3" sm="6">
                                <Widgets1
                                    data={{
                                        title: t('Failed'),
                                        total: uploadResult.upload_summary?.failed || 0,
                                        color: 'secondary',
                                        icon: 'customers'
                                    }}
                                />
                            </Col>

                        </Row>

                        {/* Failed Rows Table */}

                        {uploadResult.failed_rows && uploadResult.failed_rows.length > 0 && (
                            <>
                                <Label className="fw-bold mb-3"> {t('Failed Tenants')} </Label>

                                <DataTable
                                    data={uploadResult.failed_rows}
                                    columns={failedColumns}
                                    striped
                                    pagination
                                    highlightOnHover
                                    dense
                                />
                            </>

                        )}

                    </ModalBody>

                    <ModalFooter>

                        <Btn attrBtn={{ color: 'primary', onClick: handleBackToDashboard }}>
                            {t('OK')}
                        </Btn>

                    </ModalFooter>

                </Modal>
            )}
        </Fragment>
    );
};

export default UploadMonthlySalesView;